import { Section } from '@/components/layout';
import { Reveal } from '@/components/motion';
import { Eyebrow } from '@/components/typography';
import { GoldLine } from '@/components/ornaments';
import type { TourEvent } from '@/features/events';
import styles from './FaqBogota.module.css';

/* =============================================================================
 * S7 · FAQ LOCAL  —  ADICIÓN EXIGIDA POR EL BRIEF (ADR-006)
 * -----------------------------------------------------------------------------
 * El mockup `DEMO_2_Bogota.png` NO dibuja esta sección. La piden el master
 * prompt §7.9 y §"Estructura de cada landing de ciudad" punto 11.
 *
 * Las preguntas y las respuestas salen ENTERAS de `event.faq` (config tipada):
 * en este fichero no hay ni un horario, ni una sede, ni un precio tecleado.
 *
 * HONESTIDAD (ADR-003 §1): lo que todavía no existe —sede, precio, cambios,
 * contacto— se responde diciendo que se anunciará, y se marca con el distintivo
 * «Por anunciar». No se rellena con humo.
 *
 * ACORDEÓN `<details>/<summary>` NATIVO — patrón de FORGE-ECHO, adoptado por el
 * ORACLE para las seis páginas (25-jul). Yo defendí la FAQ abierta de par en par
 * con el argumento de que *una objeción que frena una compra no se esconde tras
 * un clic*; el patrón de ECHO cumple ese argumento MEJOR que el mío:
 *
 *   · la PREGUNTA —donde vive la objeción— sigue siempre visible; lo que se
 *     pliega es la respuesta;
 *   · la marca «Por anunciar» va en el `summary`, NO dentro de la respuesta, así
 *     que lo que ADR-003 obliga a declarar se ve **sin abrir nada**;
 *   · y se recupera la escaneabilidad que mi versión perdía: nueve respuestas
 *     abiertas eran un muro de texto seguido.
 *
 * Teclado y lector de pantalla sin una línea de JavaScript. El atributo `name`
 * compartido deja **una sola respuesta abierta a la vez**: es comportamiento
 * nativo del navegador y donde no esté soportado degrada a acordeón normal,
 * nunca a contenido inaccesible.
 * ========================================================================== */
const FAQ_ID = 'bogota-faq-titulo';

export interface FaqBogotaProps {
  event: TourEvent;
}

export function FaqBogota({ event }: FaqBogotaProps) {
  return (
    <Section background="surface" labelledBy={FAQ_ID} className={styles.section}>
      <Reveal className={styles.head}>
        <Eyebrow ink="gold" align="center">
          Antes de decidirte
        </Eyebrow>

        <h2 id={FAQ_ID} className={styles.title}>
          Preguntas frecuentes
        </h2>

        <GoldLine ornament="diamond" width="sm" className={styles.rule} />

        <p className={styles.intro}>
          Todo lo que sabemos hoy sobre el encuentro de {event.city}. Lo que aún no está cerrado lo
          decimos tal cual: se anunciará en esta misma página.
        </p>
      </Reveal>

      <Reveal className={styles.listWrap} delay={0.08}>
        <ul className={styles.list} role="list">
          {event.faq.map((item) => (
            <li className={styles.item} key={item.id}>
              <details className={styles.details} name={FAQ_ID}>
                <summary className={styles.summary}>
                  <span className={styles.question}>
                    {item.question}
                    {item.isPending ? <span className={styles.pending}>Por anunciar</span> : null}
                  </span>
                  <span className={styles.mark} aria-hidden="true" />
                </summary>
                <p className={styles.answer}>{item.answer}</p>
              </details>
            </li>
          ))}
        </ul>
      </Reveal>
    </Section>
  );
}
